import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WhatsAppButton } from "@/components/landing/WhatsAppButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageMeta, SITE_URL } from "@/seo/usePageMeta";
import { SERVICES_DATA } from "@/data/servicesData";

const slideUp = {
  initial: { y: 24 },
  whileInView: { y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const SectionHeading = ({ overline, title }) => (
  <motion.div {...slideUp}>
    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-brand-primary mb-4">{overline}</p>
    <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter leading-none text-zinc-900">{title}</h2>
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
    />
  </motion.div>
);

export default function ServicePage({ service }) {
  usePageMeta({
    title: service.metaTitle,
    description: service.metaDescription,
    path: `/services/${service.slug}/`,
  });

  useEffect(() => {
    const id = "service-schema";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.schemaName,
        description: service.metaDescription,
        url: `${SITE_URL}/services/${service.slug}/`,
        areaServed: "New Brunswick",
        provider: {
          "@type": "HomeAndConstructionBusiness",
          name: "SafeHome Security Solutions Inc.",
          telephone: "+1-506-871-4144",
          url: `${SITE_URL}/`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/#services` },
          { "@type": "ListItem", position: 3, name: service.schemaName, item: `${SITE_URL}/services/${service.slug}/` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, [service]);

  const related = SERVICES_DATA.filter((s) => service.related.includes(s.slug));

  return (
    <div data-testid={`service-page-${service.slug}`}>
      <Header />
      <main>
        <section className="relative grain">
          <div className="absolute inset-0">
            <img src={service.image} alt={service.imageAlt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/65" />
          </div>
          <div className="relative container-x py-24 lg:py-32">
            <nav data-testid="service-breadcrumb" className="flex items-center gap-1.5 text-xs sm:text-sm text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" data-testid="breadcrumb-home-link" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/#services" data-testid="breadcrumb-services-link" className="hover:text-white transition-colors">Services</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">{service.schemaName}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none text-white max-w-3xl">
              {service.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/85 max-w-xl">{service.heroText}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/#quote"
                data-testid="service-quote-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+15068714144"
                data-testid="service-call-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
              >
                <Phone className="w-4 h-4" /> (506) 871-4144
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHeading overline="Overview" title={service.overviewTitle} />
              {service.overview.map((p, i) => (
                <p key={i} className="mt-6 text-base leading-relaxed text-zinc-600">{p}</p>
              ))}
            </div>
            <img
              src={service.image}
              alt={service.imageAlt}
              loading="lazy"
              className="w-full rounded-md border border-zinc-200 object-cover aspect-[4/3]"
            />
          </div>
        </section>

        {service.types && (
          <section className="py-20 lg:py-28">
            <div className="container-x">
              <SectionHeading overline="Two Ways To Shine" title="Holiday lighting or permanent lighting" />
              <div className="mt-12 grid md:grid-cols-2 gap-6">
                {service.types.map((t) => (
                  <div key={t.title} className="bg-[#FAFAFA] border border-zinc-200 rounded-md p-8 shadow-sm">
                    <h3 className="text-xl font-bold tracking-tight text-zinc-900">{t.title}</h3>
                    <p className="mt-3 text-sm sm:text-base text-zinc-600 leading-relaxed">{t.desc}</p>
                    <ul className="mt-5 space-y-3">
                      {t.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-zinc-700">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                          <span className="text-sm sm:text-base">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-20 lg:py-28 bg-[#F5F5F4]">
          <div className="container-x">
            <SectionHeading overline="Where We Install" title="Residential & commercial applications" />            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {[
                { title: "Residential", items: service.applications.residential },
                { title: "Commercial", items: service.applications.commercial },
              ].map((group) => (
                <div key={group.title} className="bg-white border border-zinc-200 rounded-md p-8 shadow-sm">
                  <h3 className="text-xl font-bold tracking-tight text-zinc-900">{group.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-zinc-700">
                        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                        <span className="text-sm sm:text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {service.showcase && (
          <section className="py-20 lg:py-28">
            <div className="container-x">
              <SectionHeading overline="Inspiration" title="What professional exterior lighting looks like" />
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.showcase.map((img, i) => (
                  <figure
                    key={img.url}
                    data-testid={`showcase-${service.slug}-${i}`}
                    className="group relative overflow-hidden rounded-xl border border-zinc-200 aspect-[4/3]"
                  >
                    <img
                      src={img.url}
                      alt={img.alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-20 lg:py-28">
          <div className="container-x">
            <SectionHeading overline="How It Works" title="Our installation process" />
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.process.map((step, i) => (
                <div key={step.title} className="bg-white border border-zinc-200 rounded-md p-7 shadow-sm">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-brand-primary text-black font-extrabold">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 text-lg font-bold tracking-tight text-zinc-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-[#F5F5F4]">
          <div className="container-x grid lg:grid-cols-2 gap-14">
            <div>
              <SectionHeading overline="Why SafeHome" title="Benefits at a glance" />
              <ul className="mt-10 space-y-4">
                {service.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-zinc-700">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                    <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SectionHeading overline="Questions" title="Frequently asked questions" />
              <Accordion type="single" collapsible className="mt-10">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${service.slug}-${i}`}>
                    <AccordionTrigger className="text-left text-zinc-900 font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-zinc-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28">
          <div className="container-x">
            <SectionHeading overline="Keep Exploring" title="Related services" />
            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}/`}
                  data-testid={`related-service-${r.slug}`}
                  className="group bg-white border border-zinc-200 rounded-md p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60 hover:shadow-[0_8px_30px_rgba(212,175,55,0.18)]"
                >
                  <h3 className="text-lg font-bold tracking-tight text-zinc-900 group-hover:text-brand-primary transition-colors">
                    {r.name}
                  </h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                    Learn more <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-24 bg-black grain relative">
          <div className="container-x flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">Ready to get started?</h2>
              <p className="mt-3 text-white/70 text-base">Free, no-obligation quotes — call us or send the form.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/#quote"
                data-testid="service-cta-quote-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#D4AF37]/25"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+15068714144"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-white/10 border border-white/25 text-white font-semibold transition-all duration-200 hover:-translate-y-1 hover:bg-white/20"
              >
                <Phone className="w-4 h-4" /> (506) 871-4144
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
